import { useState, useEffect } from "react";
import { SuperAdminLayout } from "@/components/admin/SuperAdminLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/hooks/useAuth";
import { 
  Search, 
  Shield, 
  Users, 
  Crown,
  UserPlus
} from "lucide-react";
import { subscribeToUsers } from "@/api/users";
import { User as UserType, UserRole } from "@/lib/schema";
import { UserCard, UserKPICard, UserDetailModal, CreateUserModal } from "@/components/admin/user";

export default function UserManagement() {
  const authContext = useAuth();
  const currentUser = authContext?.userData;
  const [searchTerm, setSearchTerm] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [selectedUser, setSelectedUser] = useState<UserType | null>(null);
  const [showUserDetail, setShowUserDetail] = useState(false);
  const [showCreateUser, setShowCreateUser] = useState(false);
  const [users, setUsers] = useState<UserType[]>([]);
  const [loading, setLoading] = useState(true);

  // Subscribe to real-time user data for super_admin and recruiter roles only
  useEffect(() => {
    const unsubscribe = subscribeToUsers(
      (fetchedUsers) => {
        setUsers(fetchedUsers);
        setLoading(false);
      },
      [UserRole.SUPER_ADMIN, UserRole.RECRUITER]
    );

    return unsubscribe;
  }, []);

  const filteredUsers = users.filter(user => {
    const fullName = `${user.firstName} ${user.lastName}`.toLowerCase();
    const matchesSearch = 
      fullName.includes(searchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesRole = roleFilter === "all" || user.role === roleFilter;
    
    return matchesSearch && matchesRole;
  });

  return (
    <SuperAdminLayout 
      title="User Management"
      subtitle="Manage admin and recruiter accounts"
      maxWidth="7xl"
    >
      <div className="space-y-8">
        <div className="flex items-center justify-end">
          <Button 
            variant="primary"
            size="sm"
            onClick={() => setShowCreateUser(true)}
          >
            <UserPlus className="h-4 w-4 mr-2" />
            Add User
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <UserKPICard
            title="Total Users"
            value={users.length}
            icon={Users}
            testId="total-users-kpi"
          />
          
          <UserKPICard
            title="Recruiters"
            value={users.filter(u => u.role === UserRole.RECRUITER).length}
            icon={Shield}
            testId="recruiters-kpi"
          />

          <UserKPICard
            title="Super Admins"
            value={users.filter(u => u.role === UserRole.SUPER_ADMIN).length}
            icon={Crown}
            variant="dark"
            testId="super-admins-kpi"
          />
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="mb-6">
            {/* Mobile Layout - Stack vertically */}
            <div className="block md:hidden space-y-4">
              <div className="relative w-full">
                <Input
                  placeholder="Search users..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pr-10"
                  data-testid="search-users"
                />
                <Search className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              </div>
              
              <Select value={roleFilter} onValueChange={setRoleFilter}>
                <SelectTrigger className="w-full [&>svg]:text-[#FF7A00] [&>svg]:opacity-100" data-testid="filter-role">
                  <SelectValue placeholder="All Roles" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Roles</SelectItem>
                  <SelectItem value="super_admin">Super Admin</SelectItem>
                  <SelectItem value="recruiter">Recruiter</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Desktop Layout - Single row */}
            <div className="hidden md:flex items-center gap-4">
              <div className="relative flex-1">
                <Input
                  placeholder="Search users..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pr-10"
                  data-testid="search-users"
                />
                <Search className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              </div>
              
              <div className="flex items-center gap-4">
                <Select value={roleFilter} onValueChange={setRoleFilter}>
                  <SelectTrigger className="w-40 [&>svg]:text-[#FF7A00] [&>svg]:opacity-100" data-testid="filter-role">
                    <SelectValue placeholder="All Roles" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Roles</SelectItem>
                    <SelectItem value="super_admin">Super Admin</SelectItem>
                    <SelectItem value="recruiter">Recruiter</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {[...Array(6)].map((_, i) => (
              <Card key={i} className="animate-pulse">
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="h-12 w-12 bg-gray-200 rounded-full"></div>
                    <div className="space-y-2">
                      <div className="h-4 bg-gray-200 rounded w-24"></div>
                      <div className="h-3 bg-gray-200 rounded w-32"></div>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <div className="h-3 bg-gray-200 rounded w-20"></div>
                    <div className="h-3 bg-gray-200 rounded w-28"></div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {filteredUsers.map((user) => (
              <UserCard 
                key={user.id} 
                user={user} 
                currentUser={currentUser}
                onUserSelect={(user) => {
                  setSelectedUser(user);
                  setShowUserDetail(true);
                }}
              />
            ))}
          </div>
        )}

        {filteredUsers.length === 0 && !loading && (
          <Card>
            <CardContent className="p-12 text-center">
              <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                No users found
              </h3>
              <p className="text-gray-600">
                Try adjusting your search criteria or filters.
              </p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* User Detail Modal */}
      <UserDetailModal
        open={showUserDetail}
        onOpenChange={setShowUserDetail}
        user={selectedUser}
        currentUser={currentUser}
        onUserUpdate={() => {
          // Refresh users data when user is updated
          // The subscribeToUsers effect will automatically update the users state
        }}
      />

      {/* Create User Modal */}
      <CreateUserModal
        open={showCreateUser}
        onOpenChange={setShowCreateUser}
        onUserCreated={() => {
          // Refresh users data when user is created
          // The subscribeToUsers effect will automatically update the users state
        }}
      />

    </SuperAdminLayout>
  );
}